// Helpers tipados del MÓDULO 3 (Instrucción). Centraliza las llamadas al backend
// para que la vista de detalle no repita rutas/DTOs. Base de la API y token JWT los
// resuelve `api` (lib/api.ts). Todo requiere Bearer. Los bloqueos procesales llegan
// como error 422 con { message, code }; `api()` propaga el `message` como Error.

import { api } from './api';

// ---- Tipos de dominio (respuestas snake_case del backend) -----------------

export type EstadoFirma = 'SIN_FIRMAR' | 'EN_REVISION' | 'APROBADO' | 'FIRMADO';
export type Semaforo = 'VERDE' | 'AMARILLO' | 'ROJO' | 'NEGRO';
export type DecisionNulidad = 'NIEGA' | 'PARCIAL' | 'TOTAL';

export interface FirmaResumen {
  id?: string;
  usuario?: string | null;
  rol?: string | null;
  accion?: string | null;
  fecha?: string | null;
}

export interface Actuacion {
  id: string;
  tipo: string;
  etapa: string;
  fecha: string;
  estado_firma: EstadoFirma;
  descripcion?: string | null;
  firmas?: FirmaResumen[];
}

export interface Termino {
  id: string;
  tipo_termino: string;
  fundamento_normativo?: string | null;
  fecha_inicio?: string | null;
  fecha_vencimiento?: string | null;
  tipo_dias?: string | null;
  dias_totales?: number | null;
  estado?: string | null;
  semaforo: Semaforo;
}

export interface Prueba {
  id: string;
  tipo_prueba: string;
  solicitante?: string | null;
  estado: string;
  fecha_programada?: string | null;
  hora_programada?: string | null;
  lugar?: string | null;
  fecha_practicada?: string | null;
  observaciones?: string | null;
}

export interface Nulidad {
  id: string;
  causal: string;
  solicitante?: string | null;
  decision?: string | null;
  resuelta: boolean;
  fecha_solicitud?: string | null;
  actuacion_anulada?: string | null;
  retorna_a?: string | null;
}

// ---- Actuaciones y cadena de firma ----------------------------------------

export function getActuaciones(id: string): Promise<Actuacion[]> {
  return api<Actuacion[]>(`/expedientes/${id}/actuaciones`);
}

export interface CrearActuacionInput {
  tipo: string;
  etapa: string;
  fundamentoNormativo?: string;
  descripcion?: string;
  documentoKey?: string;
  requiereNotificacion?: boolean;
  requiereComunicacion?: boolean;
}

export function crearActuacion(id: string, body: CrearActuacionInput): Promise<Actuacion> {
  return api<Actuacion>(`/expedientes/${id}/actuaciones`, { method: 'POST', body });
}

export function enviarRevision(actuacionId: string): Promise<unknown> {
  return api(`/actuaciones/${actuacionId}/enviar-revision`, { method: 'POST' });
}

export function revisarActuacion(
  actuacionId: string,
  body: { aprobado: boolean; motivo?: string },
): Promise<unknown> {
  return api(`/actuaciones/${actuacionId}/revisar`, { method: 'POST', body });
}

export function firmarActuacion(actuacionId: string): Promise<unknown> {
  return api(`/actuaciones/${actuacionId}/firmar`, { method: 'POST' });
}

// ---- Términos --------------------------------------------------------------

export function getTerminos(id: string): Promise<Termino[]> {
  return api<Termino[]>(`/expedientes/${id}/terminos`);
}

// ---- Pruebas ---------------------------------------------------------------

export function getPruebas(id: string): Promise<Prueba[]> {
  return api<Prueba[]>(`/expedientes/${id}/pruebas`);
}

export interface CrearPruebaInput {
  tipoPrueba: string;
  solicitante?: string;
  decision?: string;
  fechaProgramada?: string;
  horaProgramada?: string;
  lugar?: string;
  observaciones?: string;
}

export function crearPrueba(id: string, body: CrearPruebaInput): Promise<Prueba> {
  return api<Prueba>(`/expedientes/${id}/pruebas`, { method: 'POST', body });
}

export interface ResultadoPruebaInput {
  estado: string;
  fechaPracticada?: string;
  soporteKey?: string;
  observaciones?: string;
}

export function registrarResultadoPrueba(
  pruebaId: string,
  body: ResultadoPruebaInput,
): Promise<Prueba> {
  return api<Prueba>(`/pruebas/${pruebaId}/resultado`, { method: 'PATCH', body });
}

// ---- Nulidades -------------------------------------------------------------

export function getNulidades(id: string): Promise<Nulidad[]> {
  return api<Nulidad[]>(`/expedientes/${id}/nulidades`);
}

export interface CrearNulidadInput {
  solicitante?: string;
  causal: string;
  fechaSolicitud?: string;
}

export function crearNulidad(id: string, body: CrearNulidadInput): Promise<Nulidad> {
  return api<Nulidad>(`/expedientes/${id}/nulidades`, { method: 'POST', body });
}

export interface ResolverNulidadInput {
  decision: DecisionNulidad;
  fecha: string;
  actuacionAnulada?: string;
  retornaA?: string;
}

export function resolverNulidad(
  nulidadId: string,
  body: ResolverNulidadInput,
): Promise<Nulidad> {
  return api<Nulidad>(`/nulidades/${nulidadId}/resolver`, { method: 'POST', body });
}

// ---- Acciones de instrucción (decisión inicial) ---------------------------

export function autoInhibitorio(
  id: string,
  body: { fecha: string; motivo: string; documentoKey?: string },
): Promise<unknown> {
  return api(`/expedientes/${id}/auto-inhibitorio`, { method: 'POST', body });
}

export function indagacionPrevia(
  id: string,
  body: { fecha: string; objeto: string; documentoKey?: string; esDdhhDih?: boolean },
): Promise<unknown> {
  return api(`/expedientes/${id}/indagacion-previa`, { method: 'POST', body });
}

export function iniciarInvestigacion(
  id: string,
  body: { fecha: string; documentoKey?: string },
): Promise<unknown> {
  return api(`/expedientes/${id}/investigacion`, { method: 'POST', body });
}

// ---- Gestión de indagación previa -----------------------------------------

export function patchIndagacion(
  indagacionId: string,
  body: { seIdentificoAutor?: boolean; esDdhhDih?: boolean },
): Promise<unknown> {
  return api(`/indagacion-previa/${indagacionId}`, { method: 'PATCH', body });
}

export function archivarIndagacion(
  indagacionId: string,
  body: { fecha: string },
): Promise<unknown> {
  return api(`/indagacion-previa/${indagacionId}/archivar`, { method: 'POST', body });
}

export function abrirInvestigacionDesdeIndagacion(indagacionId: string): Promise<unknown> {
  return api(`/indagacion-previa/${indagacionId}/abrir-investigacion`, { method: 'POST' });
}

// ---- Gestión de investigación ---------------------------------------------

export function prorrogaInvestigacion(
  investigacionId: string,
  body: { motivo?: string; dosOMasConductas?: boolean },
): Promise<unknown> {
  return api(`/investigacion/${investigacionId}/prorroga`, { method: 'POST', body });
}

export function versionLibre(
  investigacionId: string,
  body: { seRindio: boolean; documentoKey?: string },
): Promise<unknown> {
  return api(`/investigacion/${investigacionId}/version-libre`, { method: 'PATCH', body });
}

export function notificacionInvestigacion(
  investigacionId: string,
  body: { fecha: string },
): Promise<unknown> {
  return api(`/investigacion/${investigacionId}/notificacion`, { method: 'POST', body });
}

// ---- Cierre, alegatos, evaluación y remisión ------------------------------

export function cerrarInvestigacion(
  id: string,
  body: { fecha: string },
): Promise<unknown> {
  return api(`/expedientes/${id}/cerrar-investigacion`, { method: 'POST', body });
}

export function alegatos(
  id: string,
  body: {
    tipo: 'PRECALIFICATORIOS';
    presento: boolean;
    fechaPresentacion?: string;
    documentoKey?: string;
  },
): Promise<unknown> {
  return api(`/expedientes/${id}/alegatos`, { method: 'POST', body });
}

export function evaluacionArchivo(
  id: string,
  body: { fecha: string },
): Promise<unknown> {
  return api(`/expedientes/${id}/evaluacion/archivo`, { method: 'POST', body });
}

export function crearPliego(
  id: string,
  body: {
    fecha: string;
    calificacionProvisional: string;
    modalidadConducta: string;
    formaCulpabilidad: string;
  },
): Promise<{ id: string }> {
  return api<{ id: string }>(`/expedientes/${id}/pliego`, { method: 'POST', body });
}

export function notificarPliego(
  pliegoId: string,
  body: { fecha: string },
): Promise<unknown> {
  return api(`/pliego/${pliegoId}/notificar`, { method: 'POST', body });
}

export function remitirJuzgamiento(
  id: string,
  body: { fecha: string; correoDestino?: string },
): Promise<unknown> {
  return api(`/expedientes/${id}/remitir-juzgamiento`, { method: 'POST', body });
}

// ---- Sesión / RBAC de la UI -----------------------------------------------

export interface UsuarioSesion {
  id?: string;
  nombre?: string;
  rol?: string;
  [k: string]: unknown;
}

/** Lee el usuario en sesión desde localStorage ('usuario'). */
export function usuarioActual(): UsuarioSesion | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('usuario');
    return raw ? (JSON.parse(raw) as UsuarioSesion) : null;
  } catch {
    return null;
  }
}

// Roles con capacidad de firmar (Director) y de revisar (Abogado especializado).
export const ROLES_FIRMA = ['DIRECTOR_INSTRUCCION', 'ADMINISTRADOR'];
export const ROLES_REVISION = ['ABOGADO_ESPECIALIZADO', 'ADMINISTRADOR'];

export function puedeFirmar(rol?: string): boolean {
  return !!rol && ROLES_FIRMA.includes(rol);
}
export function puedeRevisar(rol?: string): boolean {
  return !!rol && ROLES_REVISION.includes(rol);
}
